import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, useLocation, useRoutes } from "react-router";
import { Spinner } from "../components/ui.tsx";
import { ChooseChildScreen } from "../screens/ChooseChild.tsx";
import { HomeScreen } from "../screens/Home.tsx";
import { RequireChild, RequireParent } from "./guards.tsx";
import { ChildShell } from "./ChildShell.tsx";
import { RouteErrorBoundary } from "./RouteError.tsx";

// First load = what a returning child sees (map, child picker); the rest on demand (T4.10, T4.10b).
const named = <K extends string>(load: () => Promise<Record<K, () => ReactNode>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const WelcomeScreen = named(() => import("../screens/Welcome.tsx"), "WelcomeScreen");
const LoginScreen = named(() => import("../screens/Login.tsx"), "LoginScreen");
const PracticeHubScreen = named(() => import("../screens/PracticeHub.tsx"), "PracticeHubScreen");
const ComingSoonScreen = named(() => import("../screens/ComingSoon.tsx"), "ComingSoonScreen");
const loadLesson = () => import("../screens/Lesson.tsx");
const LessonScreen = named(loadLesson, "LessonScreen");

// The player (exercise views, notebook) is the heaviest part: load it on demand.
const PlayScreen = lazy(() => import("../screens/play/PlayScreen.tsx"));
const MixedPlayScreen = lazy(() => import("../screens/play/MixedPlayScreen.tsx"));
const BossPlayScreen = lazy(() => import("../screens/play/BossPlayScreen.tsx"));
const SpeedScreen = lazy(() => import("../screens/play/SpeedScreen.tsx"));
const GameScreen = lazy(() => import("../screens/game/GameScreen.tsx"));
const ActivitiesScreen = lazy(() => import("../screens/Activities.tsx"));
const ParentAreaScreen = lazy(() => import("../screens/ParentArea.tsx"));
const ProgressScreen = lazy(() => import("../screens/parent/Progress.tsx"));
const AchievementsScreen = lazy(() => import("../screens/Achievements.tsx"));
const ShopScreen = lazy(() => import("../screens/Shop.tsx"));
const LeaderboardScreen = lazy(() => import("../screens/Leaderboard.tsx"));
const MyHomeScreen = lazy(() => import("../screens/MyHome.tsx"));
// dev-only content preview (tree-shaken out of production builds)
const PackGallery = import.meta.env.DEV
  ? lazy(() => import("../screens/dev/PackGallery.tsx"))
  : null;

const lazyPage = (node: ReactNode) => <Suspense fallback={<Spinner />}>{node}</Suspense>;

/**
 * Declarative routing (no loaders/actions): keeps react-router's data-router core out of the
 * first load (T4.10). Every page shows the friendly RouteError instead of a raw crash page.
 */
export function AppRouter() {
  // the lesson page is one tap from the map: fetch it in the background once the app is up
  useEffect(() => {
    const t = window.setTimeout(() => void loadLesson().catch(() => undefined), 2500);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <BrowserRouter>
      <GuardedRoutes />
    </BrowserRouter>
  );
}

function GuardedRoutes() {
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundary key={pathname}>
      <AppRoutes />
    </RouteErrorBoundary>
  );
}

function AppRoutes() {
  return useRoutes(routes());
}

function routes() {
  return [
    { path: "/", element: lazyPage(<WelcomeScreen />) },
    { path: "/dang-nhap", element: lazyPage(<LoginScreen />) },
    {
      path: "/chon-be",
      element: (
        <RequireParent>
          <ChooseChildScreen />
        </RequireParent>
      ),
    },
    {
      path: "/phu-huynh",
      element: <RequireParent>{lazyPage(<ParentAreaScreen />)}</RequireParent>,
    },
    {
      path: "/phu-huynh/tien-do/:childId",
      element: <RequireParent>{lazyPage(<ProgressScreen />)}</RequireParent>,
    },
    {
      element: (
        <RequireChild>
          <ChildShell />
        </RequireChild>
      ),
      children: [
        { path: "/ban-do", element: <HomeScreen /> },
        { path: "/bai/:lessonId", element: lazyPage(<LessonScreen />) },
        { path: "/bai/:lessonId/ngoai-doi", element: lazyPage(<ActivitiesScreen />) },
        { path: "/luyen-tap", element: lazyPage(<PracticeHubScreen />) },
        { path: "/sap-co/:section", element: lazyPage(<ComingSoonScreen />) },
        { path: "/thanh-tich", element: lazyPage(<AchievementsScreen />) },
        { path: "/xep-hang", element: lazyPage(<LeaderboardScreen />) },
        { path: "/cua-hang", element: lazyPage(<ShopScreen />) },
        { path: "/nha-cua-to", element: lazyPage(<MyHomeScreen />) },
      ],
    },
    {
      path: "/bai/:lessonId/tro-choi/:gameId",
      element: <RequireChild>{lazyPage(<GameScreen />)}</RequireChild>,
    },
    {
      path: "/bai/:lessonId/:mode",
      element: <RequireChild>{lazyPage(<PlayScreen />)}</RequireChild>,
    },
    {
      path: "/dao/:topicId/thu-thach",
      element: <RequireChild>{lazyPage(<BossPlayScreen />)}</RequireChild>,
    },
    {
      path: "/luyen-tap/dau-nhanh",
      element: <RequireChild>{lazyPage(<SpeedScreen />)}</RequireChild>,
    },
    {
      path: "/luyen-tap/on-tap",
      element: <RequireChild>{lazyPage(<MixedPlayScreen mode="review" />)}</RequireChild>,
    },
    {
      path: "/luyen-tap/hang-ngay",
      element: <RequireChild>{lazyPage(<MixedPlayScreen mode="daily" />)}</RequireChild>,
    },
    ...(PackGallery
      ? [
          {
            path: "/dev/goi/:lessonId",
            element: <RequireChild>{lazyPage(<PackGallery />)}</RequireChild>,
          },
        ]
      : []),
    { path: "*", element: <Navigate to="/" replace /> },
  ];
}
